import { Link, Typography, Tooltip, IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { DataGrid, GridColumns } from "@mui/x-data-grid"
import { useMemo, useState, useCallback, useContext } from "react";
import { CustomButton, CustomChip, SkeletonLoading, ConfirmationModal } from "@components";
import ReturnIconPath from "@assets/svg/Return.svg";
import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import MoreVert from "@mui/icons-material/MoreVert";
import ProgressCircularIconPath from "@assets/svg/ProgressCircular.svg";
import { COLORS } from "@styles";
import { appContext, formatMoney } from "@utils";
import { BillingModel, Invoice, InvoiceStatus, OrganizationBilling } from "@graphql";
import { observer } from "mobx-react";
import { RequestStatus } from "@store";
import AddIcon from '@mui/icons-material/Add';
import { toast } from "react-toastify";
import BorderColorOutlined from "@mui/icons-material/BorderColorOutlined";
import { calculatePaymentStatus, formatDate, getInvoiceBillingDate, getInvoicePlan, PaymentStatusEnum } from "../../../utils";
import { RetryPaymentDrawer } from "../RetryPaymentDrawer";
import { InvoiceRefundDrawer } from "../InvoiceRefundDrawer";

const styles = {
  dataGridContainer: {
    minHeight: '20rem',
    width: '100%',
    paddingBottom: '10rem',
  },
  dataGridHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '1rem',
    alignItems: 'center',
    color: COLORS.NEUTRAL_LIGHT_3,
    padding: '1.5rem 0',
    marginTop: '0.5rem',
  },
  styledDownloadAction: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'theme.text.primary',
    '&:hover': {
      color: COLORS.INFO_LIGHT,
    },
  },
  dataGrid: {
    '& .MuiDataGrid-columnHeader': {
      fontWeight: 600,
    },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },
  }
}

function PaymentStatus({ status }: { status: PaymentStatusEnum }) {
  if (status === PaymentStatusEnum.Paid) {
    return <CustomChip label="Paid" variant="success" icon={<Check />} small />
  }
  if (status === PaymentStatusEnum.Pending) {
    return <CustomChip
      label="Pending"
      variant="dark"
      icon={<img src={ProgressCircularIconPath} alt="progress-circular-icon" />}
      small
    />
  }

  if (status === PaymentStatusEnum.Closed) {
    return <CustomChip label="Closed" variant="dark" icon={<Close />} small />
  }

  if (status === PaymentStatusEnum.Draft) {
    return <CustomChip label="Draft" variant="help" icon={<BorderColorOutlined />} small />
  }

  return <CustomChip label="Failed" variant="error" icon={<Close />} small />
}

export const OrganizationBillingTable = observer(() => {
  const {
    organizationStore: { organizationInContext: organization },
    billingStore: {
      invoices,
      organizationBilling,
      invoicesData: { status: invoicesStatus },
      organizationBillingData: { status: organizationBillingStatus },
      manualPayInvoiceData,
      createInvoicePreview,
      createInvoicePreviewData,
      getInvoices,
      markInvoiceAsPaid,
    },
    tiersStore: { tiers },
  } = useContext(appContext);

  const isLoading = useMemo(
    () => invoicesStatus === RequestStatus.LOADING || organizationBillingStatus === RequestStatus.LOADING,
    [invoicesStatus, organizationBillingStatus]
  );

  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [isOpenRetryPaymentDrawer, setIsOpenRetryPaymentDrawer] = useState(false);
  const [isOpenInvoiceRefundDrawer, setIsOpenInvoiceRefundDrawer] = useState(false);
  const [isOpenMarkAsPaidModal, setIsOpenMarkAsPaidModal] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpenMenu = useCallback((event: React.MouseEvent<HTMLElement>, invoice: Invoice) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedInvoice(invoice);
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMenuAnchorEl(null);
  }, []);

  const handleOpenRetryPaymentDrawer = useCallback(() => {
    setIsOpenRetryPaymentDrawer(true);
    handleCloseMenu();
  }, [handleCloseMenu]);

  const handleCloseRetryPaymentDrawer = useCallback(() => setIsOpenRetryPaymentDrawer(false), []);
  // const handleOpenInvoiceRefundDrawer = useCallback(() => setIsOpenInvoiceRefundDrawer(true), []);
  const handleCloseInvoiceRefundDrawer = useCallback(() => setIsOpenInvoiceRefundDrawer(false), []);
  
  const handleOpenMarkAsPaidModal = useCallback(() => {
    setIsOpenMarkAsPaidModal(true);
    handleCloseMenu();
  }, [handleCloseMenu]);

  const handleCloseMarkAsPaidModal = useCallback(() => setIsOpenMarkAsPaidModal(false), []);
  const isInvoiceBillingModel = useMemo(() => organizationBilling?.billingModel === BillingModel.INVOICE, [organizationBilling?.billingModel]);

  const handleCreateInvoicePreview = useCallback(async () => {
    const response = await createInvoicePreview(organization?.id as string);
    if (response) {
      toast(`Invoice ${response} created successfully`, { type: 'success' });
      await getInvoices(organization?.id as string);
    }
  }, [createInvoicePreview, getInvoices, organization?.id]);

  const handleConfirmMarkAsPaid = useCallback(async () => {
    if (!selectedInvoice?.id || !organization?.id) return;
    
    const result = await markInvoiceAsPaid(selectedInvoice.id, organization.id);
    
    if (result) {
      toast('Invoice marked as paid successfully', { type: 'success' });
      await getInvoices(organization?.id as string);
      handleCloseMarkAsPaidModal();
    }
  }, [selectedInvoice?.id, markInvoiceAsPaid, getInvoices, organization?.id, handleCloseMarkAsPaidModal]);

  const columns = useMemo<GridColumns>(() => [
    {
      field: 'number',
      headerName: 'File Name',
      flex: 1,
      renderCell: (params) => {
        const invoiceNumber = params.row.number;
        if (invoiceNumber) {
          return `Invoice #${invoiceNumber}`;
        }
        return 'Invoice Draft';
      },
    },
    {
      field: 'dueDate',
      headerName: 'Billing Date',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => formatDate(getInvoiceBillingDate(params.row)),
    },
    {
      field: 'status',
      headerName: 'Payment Status',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => <PaymentStatus status={calculatePaymentStatus(params.row)} />,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const currency = params.row?.currency?.toUpperCase() ?? 'CAD';

        if (params.row.status === InvoiceStatus.paid) {
          return `${currency} ${formatMoney(params.row.amountPaid as number)}`;
        }
        if (params.row.status === InvoiceStatus.open) {
          return `${currency} ${formatMoney(params.row.amountDue as number)}`;
        }

        return `${currency} ${formatMoney(params.row.amountRemaining as number)}`;
      },
    },
    {
      field: 'subscriptionId',
      headerName: 'Plan',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => `${getInvoicePlan(params.row, tiers)} Plan`,
    },
    {
      field: 'actions',
      headerName: '',
      width: 60,
      align: 'center',
      sortable: false,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleOpenMenu(e, params.row)}
          size="small"
        >
          <MoreVert />
        </IconButton>
      ),
    },
    {
      field: 'id',
      headerName: '',
      width: 120,
      align: 'center',
      renderCell: (params) => {
        const invoiceStatus = params.row.status;
        if (invoiceStatus === InvoiceStatus.draft) {
          return <Link href={params.row.editInvoiceUrl} sx={styles.styledDownloadAction} target="_blank" rel="noopener noreferrer">Edit</Link>;
        }
        return <Link href={params.row.invoicePdf} sx={styles.styledDownloadAction} download>Download</Link>;
      },
    }
  ], [handleOpenMenu, tiers]);

  return (
    <>
    <div style={styles.dataGridContainer}>
        <SkeletonLoading isLoading={isLoading}>
          <div style={styles.dataGridHeader}>
            <Typography variant='h6' fontWeight={600}>Invoices List</Typography>
            {isInvoiceBillingModel && (
              <CustomButton
                variant='transparent'
                icon={<AddIcon />}
                width='fit-content'
                onClick={handleCreateInvoicePreview}
                isLoading={createInvoicePreviewData.status === RequestStatus.LOADING}
              >
                Create Invoice
              </CustomButton>
            )}
          </div>
        <DataGrid rows={invoices ?? []} columns={columns} sx={styles.dataGrid} />
        </SkeletonLoading>
      </div>
      <RetryPaymentDrawer
        open={isOpenRetryPaymentDrawer}
        onClose={handleCloseRetryPaymentDrawer}
        organization={organization}
        invoice={selectedInvoice}
        organizationBilling={organizationBilling as OrganizationBilling}
        error={manualPayInvoiceData.status === RequestStatus.FAILED}
        message={manualPayInvoiceData.error as string}
      />
      <InvoiceRefundDrawer
        open={isOpenInvoiceRefundDrawer}
        onClose={handleCloseInvoiceRefundDrawer}
        organization={organization}
        invoice={selectedInvoice}
        organizationBilling={organizationBilling as OrganizationBilling}
      />
      <ConfirmationModal
        icon={<Check />}
        title="Mark Invoice as Paid?"
        onClose={handleCloseMarkAsPaidModal}
        isOpen={isOpenMarkAsPaidModal}
        variant="warning"
        onConfirm={handleConfirmMarkAsPaid}
        confirmationButtonLabel="Confirm Mark as Paid"
        confirmationText={organization?.name || ''}
      >
        <p>
          This action is irreversible. Please confirm that this invoice has been paid outside the system.
        </p>
        <p>Type the organization name to confirm:</p>
        <strong>{organization?.name || ''}</strong>
      </ConfirmationModal>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {selectedInvoice && calculatePaymentStatus(selectedInvoice) === PaymentStatusEnum.Failed && (
          <MenuItem onClick={handleOpenRetryPaymentDrawer}>
            <ListItemIcon>
              <img src={ReturnIconPath} alt="return-icon" width={20} height={20} />
            </ListItemIcon>
            <ListItemText>Retry Payment</ListItemText>
          </MenuItem>
        )}
        {selectedInvoice && (
          selectedInvoice.status === InvoiceStatus.open || selectedInvoice.status === InvoiceStatus.uncollectible ? (
            <MenuItem onClick={handleOpenMarkAsPaidModal}>
              <ListItemIcon>
                <Check />
              </ListItemIcon>
              <ListItemText>Mark as Paid</ListItemText>
            </MenuItem>
          ) : (
            <Tooltip title="Only invoices with status open/uncollectible are eligible to be marked as paid" placement="left">
              <span>
                <MenuItem disabled>
                  <ListItemIcon>
                    <Check />
                  </ListItemIcon>
                  <ListItemText>Mark as Paid</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          )
        )}
      </Menu>
    </>
  )
});